import dayjs from 'dayjs';

import { DASHBOARD_TEMPLATE_KEYS } from '@/app/dashboards/constants';
import { cleanMixpanelProperties } from '@/app/dashboards/utils/dashboard-general.util';
import {
  CONTENT_REPORT_TYPES,
  CREATOR_AGGREGATE_REPORT_TYPES,
} from '@/app/dashboards/utils/reports.enum';
import {
  extractAutoExportMixpanelProperties,
  getAutoExportTimeInAmPm,
} from '@/app/dashboards/utils/sharing.utils';
import {
  contentCreator,
  extractMessageTags,
  extractTopicsAndComparisons,
  getBaseReportProperties,
  objectChanges,
  prefixObjectProperties,
} from '@/app/dashboards/utils/store.utils';
import { TEMPLATES } from '@/app/dashboards/utils/templates.enum';
import { useIntercomStore } from '@/stores/intercom';
import pinia from '@/stores/pinia';
import { UserEventTracker } from '@/utils/analytics/tracking';
import { formatDynamicDuration, TIME_UNIT_OPTIONS, UNIT_DISPLAY_OPTIONS } from '@/utils/formatters';
import { createLazyInstance } from '@/utils/lazy';

export class DashboardUserEventTracker extends UserEventTracker {
  constructor() {
    super();
    this.initialEventTriggerTime = null;
    this.intercomStore = createLazyInstance(useIntercomStore, [pinia]);
  }

  EVENT_NAMES = {
    ADD_REPORT_CLICK: 'Dashboard Add Report - Click',
    ADD_REPORT_TYPE_CLICKED: 'Dashboard Add Report - Report Type Clicked',
    ADD_REPORT_STEPS: 'Dashboard Add Report - Steps',
    CREATE_DASHBOARD_CLICK: 'Dashboard Create Dashboard - Click',
    CREATE_DASHBOARD_TEMPLATE_CLICKED: 'Dashboard Create Dashboard - Template Clicked',
    CREATE_DASHBOARD_AUTO_EXPORT: 'Dashboard Create Dashboard',
    CLICKED_SEE_MORE: 'Dashboard See More Clicked',
    CUSTOMIZE_DASHBOARD: 'Dashboard Customize Dashboard',
    DASHBOARD_VIEW_DASHBOARD: 'Dashboard View Dashboard',
    DASHBOARD_CREATE_AUTO_EXPORT: 'Dashboard Create Auto Export',
    DASHBOARD_EDIT_AUTO_EXPORT: 'Dashboard Edit Auto Export',
    DASHBOARD_DELETE_AUTO_EXPORT: 'Dashboard Delete Auto Export',
    DASHBOARD_OVERVIEW_VIEW: 'Dashboard Overview - View',
    DASHBOARD_CREATE_DASHBOARD: 'Dashboard Create Dashboard',
    DASHBOARD_SEARCH_VIEW: 'Dashboard Search - View',
    DASHBOARD_CUSTOMIZATION_ACTIONS: 'Dashboard Customization Actions',
    DASHBOARD_UPGRADE_LAYOUT: 'Dashboard Upgrade Layout',
    DASHBOARD_CREATE_REPORT: 'Dashboard Create Report',
    DASHBOARD_EDIT_REPORT: 'Dashboard Edit Report',
    DASHBOARD_DELETE_REPORT: 'Dashboard Delete Report',
    REPORT_CSV_DOWNLOAD: 'Report CSV Downloaded',
    DASHBOARD_CUSTOMIZE_LAYOUT: 'Dashboard Customize Layout',
    DASHBOARD_PDF_DOWNLOAD: 'Dashboard PDF Downloaded',
    DASHBOARD_TEMP_FILTERS: 'Dashboard Temp Filters',
    DASHBOARD_SHAREABLE_LINK_CREATED: 'Dashboard Shareable Link Created',
    DASHBOARD_SHAREABLE_LINK_DELETED: 'Dashboard Shareable Link Deleted',
    DASHBOARD_SHAREABLE_LINK_EDITED: 'Dashboard Shareable Link Edited',
    DASHBOARD_SHAREABLE_LINK_DEACTIVATED: 'Dashboard Shareable Link Deactivated',
    DASHBOARD_SHAREABLE_LINK_RENEWED: 'Dashboard Shareable Link Renewed',
  };

  addReportClick() {
    this.trackingStore.track(this.EVENT_NAMES.ADD_REPORT_CLICK, {});
  }

  addReportTypeClicked(reportType, reportFilter) {
    this.trackingStore.track(this.EVENT_NAMES.ADD_REPORT_TYPE_CLICKED, {
      reportType,
      reportTypeIndex: reportFilter,
    });
  }

  createDashboardClick() {
    this.trackingStore.track(this.EVENT_NAMES.CREATE_DASHBOARD_CLICK, {});
    this.initialEventTriggerTime = Date.now();
  }

  createDashboardTemplateClicked({ templateId }) {
    const startTime = dayjs(this.initialEventTriggerTime);
    const endTime = dayjs(Date.now());

    const eventData = {
      templateType: TEMPLATES[templateId].name,
      duration: formatDynamicDuration(
        endTime.diff(startTime),
        TIME_UNIT_OPTIONS.MILLISECOND,
        UNIT_DISPLAY_OPTIONS.SHORT,
      ),
    };

    this.trackingStore.track(this.EVENT_NAMES.CREATE_DASHBOARD_TEMPLATE_CLICKED, eventData);
  }

  dashboardCreateDashboard({ payload, newDashboard, mixpanelPayload }) {
    DASHBOARD_TEMPLATE_KEYS.forEach((key) => {
      if (newDashboard?.[key] && newDashboard?.[key]?.length > 0) {
        payload[key] = newDashboard?.[key];
      }
    });
    const {
      reportTopicsUsed,
      reportComparativeTopicsUsed,
      numberOfReportTopics,
      numberOfReportComparativeTopics,
    } = extractTopicsAndComparisons({
      topicIds: newDashboard?.templateTopicIds,
      comparisonTopicIds: newDashboard?.templateComparisonTopicIds,
      topicsFromStore: mixpanelPayload.topicsFromStore,
      comparisonsFromStore: mixpanelPayload.comparisonsFromStore,
    });

    const { reportMessageTagsUsed, numberOfReportMessageTags } = extractMessageTags({
      selectedBrandIds: newDashboard?.templateBrandIds,
      messageTagIds: newDashboard?.templateMessageTagIds,
    });
    payload.reportTopicsUsed = reportTopicsUsed;
    payload.reportComparativeTopicsUsed = reportComparativeTopicsUsed;
    payload.reportMessageTagsUsed = reportMessageTagsUsed;
    payload.reportNumberOfBrandsUsed = newDashboard?.templateBrandIds?.length || 0;
    payload.reportNumberOfCompetitorsUsed = newDashboard?.templateCompetitorIds?.length || 0;
    payload.reportNumberOfContentTagsUsed = newDashboard?.templateContentTagIds?.length || 0;
    payload.reportNumberOfMessageTagsUsed = numberOfReportMessageTags;
    payload.reportNumberOfCreatorsUsed = newDashboard?.templateCreatorIds?.length || 0;
    payload.reportNumberOfTopicsUsed = numberOfReportTopics;
    payload.reportNumberOfComparativeTopicsUsed = numberOfReportComparativeTopics;

    this.trackingStore.track(this.EVENT_NAMES.DASHBOARD_CREATE_DASHBOARD, payload);
    this.intercomStore.trackEvent(this.EVENT_NAMES.DASHBOARD_CREATE_DASHBOARD, payload);
  }

  addReportSteps({ stepNumber, stepLabel, stepTransition }) {
    const eventData = {
      stepNumber,
      stepLabel,
      stepTransition,
    };

    this.trackingStore.track(this.EVENT_NAMES.ADD_REPORT_STEPS, eventData);
  }

  seeMoreClicked(reportType, reportId, location) {
    this.trackingStore.track(this.EVENT_NAMES.CLICKED_SEE_MORE, {
      reportType,
      reportId,
      location,
    });
  }

  createAutoExport({ payload }) {
    const autoExportProperties = extractAutoExportMixpanelProperties(payload);

    this.trackingStore.track(this.EVENT_NAMES.DASHBOARD_CREATE_AUTO_EXPORT, {
      ...autoExportProperties,
    });
  }

  editAutoExport({ payload, previousPayload }) {
    const autoExportProperties = extractAutoExportMixpanelProperties(payload);

    this.trackingStore.track(this.EVENT_NAMES.DASHBOARD_EDIT_AUTO_EXPORT, {
      ...autoExportProperties,
      previousRecipientUserIds: previousPayload?.recipient_user_ids,
      previousFrequency: previousPayload?.frequency,
      previousDayOfWeek: previousPayload?.day_of_week,
      previousFormatList: previousPayload?.format_list,
      previousTimeOfDay: getAutoExportTimeInAmPm(previousPayload?.converted_time_of_day),
    });
  }

  deleteAutoExport({ payload }) {
    const autoExportProperties = extractAutoExportMixpanelProperties(payload);

    this.trackingStore.track(this.EVENT_NAMES.DASHBOARD_DELETE_AUTO_EXPORT, {
      ...autoExportProperties,
    });
  }

  dashboardViewDashboard({ payload }) {
    this.trackingStore.track(
      this.EVENT_NAMES.DASHBOARD_VIEW_DASHBOARD,
      cleanMixpanelProperties(payload),
    );
  }

  dashboardCustomizeActions({ action, layout, dashboard }) {
    this.trackingStore.track(this.EVENT_NAMES.DASHBOARD_CUSTOMIZATION_ACTIONS, {
      customAction: action,
      dashboardName: dashboard?.name,
      dashboardId: dashboard?.id,
      reportType: layout?.type,
    });
  }

  dashboardUpgradeLayout({ dashboard, oldLayout }) {
    this.trackingStore.track(this.EVENT_NAMES.DASHBOARD_UPGRADE_LAYOUT, {
      dashboardName: dashboard?.name,
      dashboardId: dashboard?.id,
      oldLayout,
    });
  }

  dashboardCreateReport({
    reportId,
    reportType,
    reportMeta,
    dashboard,
    messageTagFilterOptions,
    duplicate = false,
  }) {
    const properties = getBaseReportProperties(
      reportId,
      reportType,
      reportMeta,
      dashboard,
      messageTagFilterOptions,
    );

    properties.duplicate = duplicate;

    if (CONTENT_REPORT_TYPES.includes(reportType)) {
      properties.contentCreator = contentCreator(reportMeta?.channel);
    }

    const cleanedProperties = cleanMixpanelProperties(properties);
    // Special report aggregate case for Creator report aggregate by brand
    if (
      reportMeta.aggregate_by === 'BRAND' &&
      CREATOR_AGGREGATE_REPORT_TYPES.includes(reportType)
    ) {
      cleanedProperties.reportAggregateBy = 'CREATOR';
    }

    this.trackingStore.track(this.EVENT_NAMES.DASHBOARD_CREATE_REPORT, cleanedProperties);
  }

  dashboardEditReport({
    reportId,
    reportType,
    firstMeta,
    previousReportMeta,
    dashboard,
    inlineEdit,
    messageTagFilterOptions,
  }) {
    const currentProperties = getBaseReportProperties(
      reportId,
      reportType,
      firstMeta,
      dashboard,
      messageTagFilterOptions,
    );

    const prevProperties = getBaseReportProperties(
      reportId,
      reportType,
      previousReportMeta,
      dashboard,
      messageTagFilterOptions,
      true,
    );

    const properties = {
      ...prevProperties,
      ...currentProperties,
      ...prefixObjectProperties(objectChanges(firstMeta, previousReportMeta), 'changedReport'),
      inlineEdit,
    };

    this.trackingStore.track(this.EVENT_NAMES.DASHBOARD_EDIT_REPORT, properties);
  }

  dashboardDeleteReport({ reportId, reportType, reportMeta, dashboard }) {
    const properties = getBaseReportProperties(reportId, reportType, reportMeta, dashboard);

    if (CONTENT_REPORT_TYPES.includes(reportType)) {
      properties.contentCreator = contentCreator(reportMeta?.channel);
    }

    this.trackingStore.track(this.EVENT_NAMES.DASHBOARD_DELETE_REPORT, properties);
  }

  dashboardReportCSVDownload({ payload, dashboardProperties, messageTagFilterOptions }) {
    const currentProperties = getBaseReportProperties(
      payload.reportId,
      payload.reportType,
      payload.meta,
      dashboardProperties,
      messageTagFilterOptions,
    );

    const properties = {
      ...payload,
      ...currentProperties,
    };

    this.trackingStore.track(
      this.EVENT_NAMES.REPORT_CSV_DOWNLOAD,
      cleanMixpanelProperties(properties),
    );
  }

  dashboardCustomizeLayout({ dashboardId, dashboardName }) {
    this.trackingStore.track(this.EVENT_NAMES.DASHBOARD_CUSTOMIZE_LAYOUT, {
      dashboardId,
      dashboardName,
    });
  }

  dashboardPDFDownload({ payload }) {
    this.trackingStore.track(
      this.EVENT_NAMES.DASHBOARD_PDF_DOWNLOAD,
      cleanMixpanelProperties(payload),
    );
  }

  dashboardTempFilters({ payload }) {
    this.trackingStore.track(
      this.EVENT_NAMES.DASHBOARD_TEMP_FILTERS,
      cleanMixpanelProperties(payload),
    );
  }

  shareableLinkCreated({ payload }) {
    this.trackingStore.track(this.EVENT_NAMES.DASHBOARD_SHAREABLE_LINK_CREATED, payload);
  }

  shareableLinkEdited({ shareableLinkId, payload }) {
    this.trackingStore.track(this.EVENT_NAMES.DASHBOARD_SHAREABLE_LINK_EDITED, {
      shareableLinkId,
      ...payload,
    });
  }

  shareableLinkDeleted({ shareableLinkId }) {
    this.trackingStore.track(this.EVENT_NAMES.DASHBOARD_SHAREABLE_LINK_DELETED, {
      shareableLinkId,
    });
  }

  shareableLinkDeactivated({ shareableLinkId }) {
    this.trackingStore.track(this.EVENT_NAMES.DASHBOARD_SHAREABLE_LINK_DEACTIVATED, {
      shareableLinkId,
    });
  }

  shareableLinkRenewed({ shareableLinkId }) {
    this.trackingStore.track(this.EVENT_NAMES.DASHBOARD_SHAREABLE_LINK_RENEWED, {
      shareableLinkId,
    });
  }
}
